import axios from 'axios'
import { getConfig } from '../../../config'
import { fullNotificationApi, silentApi, getAccessToken } from '.'

interface I_Signin {
  email: string
  password: string
}

// Hard ceiling on the refresh RPC. A hung backend used to stall every awaiting
// request forever — UI froze with no feedback. After this timeout fires we
// abort the request, surface the failure to the caller, and let the interceptor
// either retry or drop to the login screen depending on bootstrap-mode.
export const REFRESH_RPC_TIMEOUT_MS = 10_000

// Safely read a token from localStorage. Historically tokens were stored as
// JSON-encoded strings (e.g. `"abc.def.ghi"`) but older entries or partial
// writes may leave a raw string behind. This helper never throws and always
// returns a usable string token, or null if nothing sensible is present.
export const readStoredToken = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem(key)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return typeof parsed === 'string' ? parsed : null
  } catch {
    return typeof raw === 'string' ? raw : null
  }
}

// Shape of the admin entity returned by /admin/login and /admin/auth/refresh.
export interface AdminSessionUser {
  _id: string
  email: string
  role: string
  sid?: string
  exp?: number
}

// Response envelope (after silentApi / fullNotificationApi unwrap response.data.data)
// for both /admin/login and /admin/auth/refresh — they share the same shape.
export interface AdminRefreshResponse {
  admin: AdminSessionUser
  token: string
  refresh_token: string
  // CSRF token mirrored in the response body because cross-domain SPAs can't read
  // the JS-readable `csrf` cookie via document.cookie. The SPA stores this in
  // memory and echoes it on subsequent /admin/auth/refresh calls as
  // `X-CSRF-Token`. Optional only because legacy backends pre-dating this change
  // may not include it; in that case the SPA falls back to readCsrfCookie().
  csrf_token?: string
}

export type AdminLoginResponse = AdminRefreshResponse

export const signin = async (data: I_Signin) => {
  return await fullNotificationApi.post('/admin/login', data)
}

// Lightweight "am I logged in?" check that looks at the in-memory access token's
// exp claim. Returns false if the token is missing, malformed, or expired.
// This is NOT a security boundary — it just avoids leaving the UI in a
// logged-in state after the token has already expired (e.g. laptop sleep).
// Reads via getAccessToken() which prefers the in-memory value and falls back
// to a one-shot migration from legacy localStorage['token'].
export const hasValidAccessToken = (): boolean => {
  const token = getAccessToken()
  if (!token) return false
  try {
    const payloadB64 = token.split('.')[1]
    if (!payloadB64) return false
    const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown }
    const rawExp = typeof payload.exp === 'number' ? payload.exp : 0
    // Backend emits seconds (JWT standard); tolerate ms if anyone ever stores it that way.
    const expMs = rawExp > 1e12 ? rawExp : rawExp * 1000
    return expMs > Date.now()
  } catch {
    return false
  }
}

// Decodes a JWT payload client-side without verifying the signature.
// Returns the `exp` claim in seconds if present.
export const decodeJwtExp = (jwt: string): number | undefined => {
  try {
    const parts = jwt.split('.')
    if (parts.length < 2) return undefined
    const payload = JSON.parse(
      decodeURIComponent(
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
    ) as { exp?: unknown }
    return typeof payload?.exp === 'number' ? payload.exp : undefined
  } catch (err) {
    console.warn('decodeJwtExp failed', err)
    return undefined
  }
}

// GET /admin/auth/csrf — recovery endpoint. Cross-domain admin SPA can't read
// the csrf cookie via document.cookie, and the localStorage mirror can drift
// out of sync with the cookie when a rotation response is interrupted (system
// sleep, network blip, multi-tab race, stale bundle cache). This endpoint
// reads the cookie server-side and hands the value back so we can retry the
// refresh with a header that matches.
//
// Tagged with `_bootstrapRefresh: true` so the response interceptor doesn't
// recover from a 401 by firing yet another refresh — that path would loop.
const fetchCsrfRecovery = async (): Promise<string | null> => {
  try {
    const response = (await silentApi.get('/admin/auth/csrf', { _bootstrapRefresh: true } as Record<
      string,
      unknown
    >)) as unknown as { csrf_token?: string } | undefined
    const csrf = response?.csrf_token
    return typeof csrf === 'string' && csrf.length > 0 ? csrf : null
  } catch {
    return null
  }
}

// POST /admin/auth/refresh — exchanges a refresh token for a new access token.
// Uses silentApi so no toast is shown; the 401 handler in index.ts drives UX.
//
// Cookie-first: in the cookie flow the httpOnly `refresh_token_admin` cookie
// (path `/`) carries the token and the body can be empty, but the backend
// requires the CSRF header (`X-CSRF-Token`) to match the `csrf` cookie.
// Callers derive the CSRF value from in-memory store / localStorage / a
// recovery fetch and pass it through.
//
// Body fallback: during the migration window some sessions may still have a
// legacy refresh token in localStorage but no cookie set — we send it in the
// body so those sessions keep working until their next login.
//
// Recovery: on 403 "Invalid CSRF token", we fetch the current csrf via
// /admin/auth/csrf and retry once. Covers sleep mid-rotation, multi-tab race,
// cleared localStorage, and bundle cache scenarios.
export const refreshAccessToken = async (
  refreshToken: string | null,
  csrfToken?: string | null,
  options?: { bootstrap?: boolean }
): Promise<AdminRefreshResponse> => {
  // `refreshToken` here is the raw localStorage value (may be JSON-encoded).
  // Use the robust reader to tolerate legacy / non-JSON entries.
  let parsed: string | null = null
  if (refreshToken) {
    try {
      const maybe = JSON.parse(refreshToken)
      parsed = typeof maybe === 'string' ? maybe : refreshToken
    } catch {
      parsed = refreshToken
    }
  }

  // Cookie flow: send empty body; backend reads the refresh token from the
  // httpOnly cookie and requires the X-CSRF-Token header to echo csrf.
  // Legacy flow: if a body token is present, include it so the backend can
  // fall back to body-mode (which bypasses CSRF server-side).
  const body: Record<string, string> = {}
  if (parsed) body.refreshToken = parsed

  // Bound the refresh RPC with an AbortController so a hung backend can't
  // freeze the UI forever. The timeout is cleared in `finally` regardless of
  // success / failure / abort.
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_RPC_TIMEOUT_MS)

  const post = async (csrf: string | null | undefined) => {
    const headers: Record<string, string> = {}
    if (csrf) headers['X-CSRF-Token'] = csrf
    return (await silentApi.post('/admin/auth/refresh', body, {
      headers,
      signal: controller.signal,
      _bootstrapRefresh: options?.bootstrap === true,
    } as Record<string, unknown>)) as unknown as AdminRefreshResponse
  }

  try {
    return await post(csrfToken)
  } catch (err: unknown) {
    // The response interceptor rejects 403 with the AxiosResponse; check the
    // error shape rather than relying on a Status field on success path.
    const errResp = err as { status?: number; data?: { message?: string } } | undefined
    const isCsrfFailure = errResp?.status === 403 && errResp?.data?.message === 'Invalid CSRF token'
    if (isCsrfFailure) {
      const recovered = await fetchCsrfRecovery()
      if (recovered) {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { setCsrfToken } = require('./index') as typeof import('./index')
        setCsrfToken(recovered)
        return await post(recovered)
      }
    }
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

// Best-effort server-side session revocation. Uses a bare axios instance with
// a short timeout so local cleanup never waits on a slow network.
// `withCredentials: true` so the backend can clear the httpOnly refresh cookie.
export const revokeAdminSession = async () => {
  try {
    const parsedToken = getAccessToken()
    if (!parsedToken) return
    await axios.post(
      `${getConfig()?.apiUrl}/admin/logout`,
      {},
      {
        timeout: 2000,
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${parsedToken}`,
        },
      }
    )
  } catch (err) {
    console.warn('admin logout revoke failed (non-fatal)', err)
  }
}

// --- Active-sessions management -------------------------------------------
// Shape returned by GET /admin/auth/sessions. Mirrors SessionViewDto on the
// backend; keep in sync if the envelope grows fields.
export interface AdminSessionView {
  _id: string
  deviceLabel?: string
  userAgent?: string
  ip?: string
  lastSeenAt: string
  lastUsedAt: string
  createdAt: string
  current: boolean
}

export interface AdminSessionsResponse {
  sessions: AdminSessionView[]
}

export const listAdminSessions = async (): Promise<AdminSessionsResponse> => {
  // fullNotificationApi shows error toasts (no success toast on GETs since the
  // response has no `message`), which is the right default for a data fetch.
  const response = (await fullNotificationApi.get('/admin/auth/sessions')) as unknown as AdminSessionsResponse
  return response
}

export const revokeAdminSessionById = async (id: string): Promise<{ message: string }> => {
  const response = (await fullNotificationApi.delete(`/admin/auth/sessions/${id}`)) as unknown as { message: string }
  return response
}

export const logoutAllAdminSessions = async (): Promise<{
  message: string
  revokedCount: number
}> => {
  const response = (await fullNotificationApi.post('/admin/auth/logout-all')) as unknown as {
    message: string
    revokedCount: number
  }
  return response
}

// POST /admin/auth/logout-others — revokes every OTHER admin session for the caller
// but keeps the current one alive. Use for the "Sign out of all other devices" button.
export const logoutOtherAdminSessions = async (): Promise<{
  message: string
  revokedCount: number
}> => {
  const response = (await fullNotificationApi.post('/admin/auth/logout-others')) as unknown as {
    message: string
    revokedCount: number
  }
  return response
}

// Persist post-login identity/expiry. Access token lives in-memory only
// (set via setAccessToken in the loginAdmin thunk). The httpOnly refresh
// cookie is set by the backend on /admin/login — the SPA must never receive
// or persist the raw refresh token. `tokenExpireAt` is still stored because
// the proactive refresh timer reads it on reload/visibility-change.
export const authenticate = (tokenExpireAt?: string | number, id?: string, role?: string) => {
  try {
    if (typeof window !== 'undefined') {
      if (role) localStorage.setItem('role', JSON.stringify(role))
      if (id) localStorage.setItem('id', JSON.stringify(id))
      if (tokenExpireAt !== undefined && tokenExpireAt !== null) {
        localStorage.setItem('tokenExpireAt', JSON.stringify(tokenExpireAt))
      }

      const storedId = localStorage.getItem('id')
      return storedId !== null
    }
  } catch (error) {
    console.error('authenticate error', error)
  }
}
